import type {
  AccessListItem,
  AccessListName,
  AccessListPaging,
  AccessRowTypes,
  DraftGroup,
  DraftRole,
  GroupWrite,
  RoleKind,
  RoleWrite,
} from '@app/application/user-management/types'

import {
  ACCESS_LISTS,
  DESTROY,
} from '@app/application/user-management/constants'
import { isDraftRole } from '@app/application/user-management/predicates'

// A role the user just added has no id yet, so the company it points at keys
// the row instead.
export const roleKey = (role: DraftRole): AccessListItem['key'] =>
  role.id ?? `company-${role.companyId}`

export const roleItems = (roles: DraftRole[]): AccessListItem[] =>
  roles.map((role) => ({
    key: roleKey(role),
    title: role.companyName,
    removable: true,
  }))

export type DraftRoleList = 'firmRoles' | 'externalRoles' | 'customerRoles'

export const ROLE_LISTS: DraftRoleList[] = [
  'firmRoles',
  'externalRoles',
  'customerRoles',
]

export type AccessRow = DraftRole | DraftGroup

// A list holds the rows loaded from the server so far, and the rows added and
// removed in the panel since. Only the added and removed rows reach the save.
export interface AccessListState<T extends AccessRow> {
  rows: T[]
  added: T[]
  removed: T[]
  query: string
  page: number
  totalPages: number
  // The server's count for `query`, and for the whole list.
  matchCount: number
  count: number
  loading: boolean
  loadingMore: boolean
  loaded: boolean
  failed: boolean
}

export type AccessLists = {
  [L in AccessListName]: AccessListState<AccessRowTypes[L]>
}

// The added rows come first, so a new row shows without paging.
export interface AccessListView<T extends AccessRow> extends AccessListPaging {
  rows: T[]
}

export type AccessListViews = {
  [L in AccessListName]: AccessListView<AccessRowTypes[L]>
}

const rowKey = (row: AccessRow): AccessListItem['key'] =>
  isDraftRole(row) ? roleKey(row) : row.id

const rowTitle = (row: AccessRow): string =>
  isDraftRole(row) ? row.companyName : row.name

// The same match as the server's `ILIKE`, for the rows it does not hold yet.
const matches = (row: AccessRow, query: string): boolean =>
  rowTitle(row).toLowerCase().includes(query.trim().toLowerCase())

// A user being created has nothing saved, so their lists start complete.
const emptyList = <T extends AccessRow>(
  saved: boolean
): AccessListState<T> => ({
  rows: [],
  added: [],
  removed: [],
  query: '',
  page: 0,
  totalPages: 0,
  matchCount: 0,
  count: 0,
  loading: false,
  loadingMore: false,
  loaded: !saved,
  failed: false,
})

export const ACCESS_LIST_NAMES = Object.keys(ACCESS_LISTS) as AccessListName[]

// One entry per access list. `Object.fromEntries` drops the type each list
// has, so the result is cast back. Each caller builds an entry from that list's
// own state, which keeps the cast true.
const perList = <R>(build: (name: AccessListName) => unknown): R =>
  Object.fromEntries(ACCESS_LIST_NAMES.map((name) => [name, build(name)])) as R

export const emptyLists = (saved: boolean): AccessLists =>
  perList(() => emptyList(saved))

// A list's label holds its count, which is unknown until the first page lands.
export const accessCount = (view: AccessListPaging): number | string =>
  view.loaded ? view.count : '…'

const viewOf = <T extends AccessRow>(
  list: AccessListState<T>
): AccessListView<T> => {
  const removedKeys = new Set(list.removed.map(rowKey))
  const matching = (rows: T[]): T[] =>
    rows.filter((row) => matches(row, list.query))

  return {
    rows: [
      ...matching(list.added),
      ...list.rows.filter((row) => !removedKeys.has(rowKey(row))),
    ],
    count: list.count + list.added.length - list.removed.length,
    matchCount:
      list.matchCount +
      matching(list.added).length -
      matching(list.removed).length,
    hasMore: list.page < list.totalPages,
    loading: list.loading,
    loadingMore: list.loadingMore,
    loaded: list.loaded,
    failed: list.failed,
    query: list.query,
  }
}

export const viewsOf = (lists: AccessLists): AccessListViews =>
  perList((name) => viewOf<AccessRow>(lists[name]))

export const removeRow = <T extends AccessRow>(
  list: AccessListState<T>,
  key: AccessListItem['key']
): void => {
  const addedIndex = list.added.findIndex((row) => rowKey(row) === key)

  if (addedIndex >= 0) {
    list.added.splice(addedIndex, 1)

    return
  }

  const row = list.rows.find((candidate) => rowKey(candidate) === key)

  if (row) list.removed.push(row)
}

export const roleWrites = (
  kind: RoleKind,
  list: AccessListState<DraftRole>
): RoleWrite[] => [
  ...list.added.map((role) => ({ companyId: role.companyId, role: kind })),
  ...list.removed.map((role) => ({
    id: role.id as number,
    role: kind,
    _destroy: DESTROY,
  })),
]

export const groupWrites = (
  list: AccessListState<DraftGroup>
): GroupWrite[] => [
  ...list.added.map((group) => ({ id: group.id })),
  ...list.removed.map((group) => ({ id: group.id, _destroy: DESTROY })),
]
